import { AlignLeft, AlertCircle, ArrowUp, FileText, Github, Plus, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import { addRepositoryContext, addTextContext, completeContextUpload, createContextUpload, fetchContextSources } from '../lib/api';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import type { ContextSource } from '../types';

type Props = {
  initialRepositoryUrl?: string;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  embedded?: boolean;
};

const acceptedFiles = '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown';

function SourceIcon({ source }: { source: ContextSource }) {
  if (source.sourceType === 'text') return <AlignLeft size={17} />;
  if (source.sourceType === 'file') return <FileText size={17} />;
  return <Github size={17} />;
}

export default function ContextLibrary({ initialRepositoryUrl = '', selectedIds, onSelectedIdsChange, embedded = false }: Props) {
  const { getToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [sources, setSources] = useState<ContextSource[]>([]);
  const [text, setText] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState(initialRepositoryUrl);
  const [repositoryOpen, setRepositoryOpen] = useState(Boolean(initialRepositoryUrl));
  const [menuOpen, setMenuOpen] = useState(false);
  const [autoSelectIds, setAutoSelectIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function refresh() {
    const token = await getToken();
    setSources((await fetchContextSources(token)).contextSources);
  }

  useEffect(() => { void refresh().catch(() => setError('Could not load saved context.')); }, []);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const height = Math.min(Math.max(textarea.scrollHeight, 86), 320);
    textarea.style.height = `${height}px`;
    textarea.style.overflowY = textarea.scrollHeight > 320 ? 'auto' : 'hidden';
  }, [text]);

  useEffect(() => {
    if (!sources.some((source) => source.status === 'pending' || source.status === 'processing')) return;
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [sources]);

  useEffect(() => {
    const finishedIds = autoSelectIds.filter((id) => sources.some((source) => source.id === id && ['ready', 'failed'].includes(source.status)));
    if (!finishedIds.length) return;
    const readyIds = finishedIds.filter((id) => sources.some((source) => source.id === id && source.status === 'ready'));
    const additions = readyIds.filter((id) => !selectedIds.includes(id)).slice(0, Math.max(0, 5 - selectedIds.length));
    if (additions.length) {
      onSelectedIdsChange([...selectedIds, ...additions]);
      additions.forEach((id) => {
        const source = sources.find((item) => item.id === id);
        if (source) void trackJourney(getToken, 'context_selected', { properties: { sourceType: source.sourceType } });
      });
    }
    setAutoSelectIds((current) => current.filter((id) => !finishedIds.includes(id)));
  }, [autoSelectIds, getToken, onSelectedIdsChange, selectedIds, sources]);

  function canAttach() {
    if (selectedIds.length + autoSelectIds.length < 5) return true;
    setError('You can include up to five context sources.');
    return false;
  }

  function queueSource(source: ContextSource) {
    setSources((current) => [...current.filter((item) => item.id !== source.id), source]);
    setAutoSelectIds((current) => [...current, source.id]);
  }

  async function addWrittenContext() {
    if (!text.trim() || busy || !canAttach()) return;
    setBusy(true); setError('');
    try {
      const token = await getToken();
      const { contextSource } = await addTextContext(token, text.trim());
      queueSource(contextSource);
      setText('');
      void trackJourney(getToken, 'context_created', { properties: { sourceType: 'text' } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Written context could not be added.');
    } finally { setBusy(false); }
  }

  async function addRepository() {
    if (!repositoryUrl.trim() || busy || !canAttach()) return;
    setBusy(true); setError('');
    try {
      const token = await getToken();
      const { contextSource } = await addRepositoryContext(token, repositoryUrl.trim());
      queueSource(contextSource);
      setRepositoryUrl('');
      setRepositoryOpen(false);
      void trackJourney(getToken, 'context_created', { properties: { sourceType: 'public_repository' } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Repository could not be added.');
    } finally { setBusy(false); }
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy || !canAttach()) return;
    setBusy(true); setError('');
    try {
      const token = await getToken();
      const created = await createContextUpload(token, file);
      const uploadResponse = await fetch(created.upload.url, {
        method: created.upload.method,
        headers: created.upload.headers,
        body: file,
      });
      if (!uploadResponse.ok) throw new Error('File upload failed.');
      const completed = await completeContextUpload(token, created.contextSource.id);
      queueSource(completed.contextSource);
      void trackJourney(getToken, 'context_created', { properties: { sourceType: 'file' } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'File could not be uploaded.');
    } finally { setBusy(false); }
  }

  function toggle(source: ContextSource) {
    if (autoSelectIds.includes(source.id)) setAutoSelectIds((current) => current.filter((id) => id !== source.id));
    else if (selectedIds.includes(source.id)) onSelectedIdsChange(selectedIds.filter((id) => id !== source.id));
    else if (source.status !== 'ready') return;
    else if (canAttach()) {
      onSelectedIdsChange([...selectedIds, source.id]);
      void trackJourney(getToken, 'context_selected', { properties: { sourceType: source.sourceType } });
    }
  }

  const attachedIds = [...selectedIds, ...autoSelectIds.filter((id) => !selectedIds.includes(id))];
  const attachedSources = attachedIds
    .map((id) => sources.find((source) => source.id === id))
    .filter((source): source is ContextSource => Boolean(source));

  return (
    <section className={embedded ? 'context-panel embedded-context-panel' : 'panel context-panel'} id="context-library">
      <div className="section-heading compact"><h2>Interview context</h2></div>
      <div className="context-composer">
        {attachedSources.length > 0 && <div className="context-assets">{attachedSources.map((source) => {
          const selected = selectedIds.includes(source.id) || autoSelectIds.includes(source.id);
          const ready = source.status === 'ready';
          return <button key={source.id} type="button" className={`context-asset ${selected ? 'selected' : ''}`} onClick={() => toggle(source)} title="Remove from this interview">
            <span className="context-asset-icon"><SourceIcon source={source} /></span>
            <span className="context-asset-copy"><strong>{source.name}</strong>{!ready && <small>{source.status === 'failed' ? 'Error' : 'Processing...'}</small>}</span>
            <span className={`context-asset-state ${source.status}`}><X size={14} /></span>
          </button>;
        })}</div>}
        <textarea ref={textareaRef} value={text} onChange={(event) => setText(event.target.value)} maxLength={12000} rows={4} placeholder="Paste a job description, earlier interview round summaries or transcripts, or describe your project and outcomes..." aria-label="Written interview context" />
        {repositoryOpen && <div className="context-repository-row"><Github size={17} /><input autoFocus type="url" value={repositoryUrl} onChange={(event) => setRepositoryUrl(event.target.value)} placeholder="https://github.com/your-name/project" aria-label="Public GitHub or GitLab repository" onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addRepository(); } }} /><button type="button" onClick={() => void addRepository()} disabled={!repositoryUrl.trim() || busy}>Add</button></div>}
        <div className="context-composer-footer">
          <div className="context-add-wrap">
            <button type="button" className="context-add-button" onClick={() => setMenuOpen((open) => !open)} aria-label="Add interview context" aria-expanded={menuOpen}><Plus size={19} /></button>
            {menuOpen && <div className="context-add-menu">
              <button type="button" onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}><FileText size={17} /><span><strong>Files</strong><small>PDF, DOCX, TXT, or MD</small></span></button>
              <button type="button" onClick={() => { setMenuOpen(false); setRepositoryOpen(true); }}><Github size={17} /><span><strong>Public git</strong><small>GitHub or GitLab URL</small></span></button>
            </div>}
          </div>
          <input ref={fileInputRef} className="context-file-input" type="file" accept={acceptedFiles} onChange={(event) => void upload(event)} disabled={busy} />
          <button type="button" className="context-submit" onClick={() => void addWrittenContext()} disabled={!text.trim() || busy} aria-label="Add written context"><ArrowUp size={18} /></button>
        </div>
      </div>
      {error && <p className="context-error"><AlertCircle size={14} /> {error}</p>}
    </section>
  );
}
